import { _ } from '/@/locale';
import type { CropProtectionMonth, Option, PhenologyIconKey } from '/@/types/crop-protection';

type PhenologyStage = {
    icons: [PhenologyIconKey];
    iconsDescriptions: [string];
};

export const getSowingPhenologyForCrop = (crop?: string): PhenologyStage => {
    if (crop === 'soy') {
        return {
            icons: ['soy_bean'],
            iconsDescriptions: [_('Siembra')],
        };
    }
    if (crop === 'wheat' || crop === 'barley') {
        return {
            icons: ['wheat_seed'],
            iconsDescriptions: [_('Siembra')],
        };
    }
    return {
        icons: ['seed'],
        iconsDescriptions: [_('Siembra')],
    };
};

const wheatStages = {
    sowing: {
        icons: ['wheat_seed'],
        iconsDescriptions: [_('Siembra')],
    },
    germination: {
        icons: ['wheat_germination'],
        iconsDescriptions: [_('Germinación')],
    },
    emergence: {
        icons: ['wheat_emergence'],
        iconsDescriptions: [_('Emergencia')],
    },
} satisfies Record<string, PhenologyStage>;

const buildWheatPreEmergentMonths = (option: Option): CropProtectionMonth[] => [
    {
        disabled: false,
        columns: option.columns,
        icons: wheatStages.sowing.icons,
        iconsDescriptions: wheatStages.sowing.iconsDescriptions,
    },
    {
        disabled: true,
        icons: wheatStages.germination.icons,
        iconsDescriptions: wheatStages.germination.iconsDescriptions,
    },
    {
        disabled: true,
        icons: wheatStages.emergence.icons,
        iconsDescriptions: wheatStages.emergence.iconsDescriptions,
    },
];

const buildWheatEarlyPoeMonths = (option: Option): CropProtectionMonth[] => [
    {
        disabled: true,
        icons: wheatStages.sowing.icons,
        iconsDescriptions: wheatStages.sowing.iconsDescriptions,
    },
    {
        disabled: false,
        columns: option.columns,
        icons: wheatStages.germination.icons,
        iconsDescriptions: wheatStages.germination.iconsDescriptions,
    },
    {
        disabled: true,
        icons: wheatStages.emergence.icons,
        iconsDescriptions: wheatStages.emergence.iconsDescriptions,
    },
];

const buildWheatLatePoeMonths = (option: Option): CropProtectionMonth[] => [
    {
        disabled: true,
        icons: wheatStages.sowing.icons,
        iconsDescriptions: wheatStages.sowing.iconsDescriptions,
    },
    {
        disabled: true,
        icons: wheatStages.germination.icons,
        iconsDescriptions: wheatStages.germination.iconsDescriptions,
    },
    {
        disabled: false,
        columns: option.columns,
        icons: wheatStages.emergence.icons,
        iconsDescriptions: wheatStages.emergence.iconsDescriptions,
    },
];

type WeedPhenologyBuilder = (option: Option) => CropProtectionMonth[];

const soyStages = {
    preEmergence: {
        icons: ['soy_bean'],
        iconsDescriptions: [_('Pre-emergencia')],
    },
    vc: {
        icons: ['soy_vc'],
        iconsDescriptions: ['VC'],
    },
    v1: {
        icons: ['soy_v1'],
        iconsDescriptions: ['V1'],
    },
    v2: {
        icons: ['soy_v2'],
        iconsDescriptions: ['V2'],
    },
} satisfies Record<string, PhenologyStage>;

const buildSoyEarlyPoeMonths = (option: Option): CropProtectionMonth[] => [
    {
        disabled: false,
        columns: option.columns,
        icons: soyStages.vc.icons,
        iconsDescriptions: soyStages.vc.iconsDescriptions,
    },
    {
        disabled: true,
        icons: soyStages.v1.icons,
        iconsDescriptions: soyStages.v1.iconsDescriptions,
    },
    {
        disabled: true,
        icons: soyStages.v2.icons,
        iconsDescriptions: soyStages.v2.iconsDescriptions,
    },
];

const buildSoyPreEmergentMonths = (option: Option): CropProtectionMonth[] => [
    {
        disabled: false,
        columns: option.columns,
        icons: soyStages.preEmergence.icons,
        iconsDescriptions: soyStages.preEmergence.iconsDescriptions,
    },
    {
        disabled: true,
        icons: soyStages.vc.icons,
        iconsDescriptions: soyStages.vc.iconsDescriptions,
    },
    {
        disabled: true,
        icons: soyStages.v1.icons,
        iconsDescriptions: soyStages.v1.iconsDescriptions,
    },
    {
        disabled: true,
        icons: soyStages.v2.icons,
        iconsDescriptions: soyStages.v2.iconsDescriptions,
    },
];

const buildSoyFallowMonths = (option: Option): CropProtectionMonth[] => {
    const sowing = getSowingPhenologyForCrop('soy');
    return [
        {
            disabled: false,
            columns: option.columns,
        },
        {
            disabled: true,
            icons: sowing.icons,
            iconsDescriptions: sowing.iconsDescriptions,
        },
    ];
};

const cerealPhenology = {
    pre_emergent: buildWheatPreEmergentMonths,
    early_poe: buildWheatEarlyPoeMonths,
    late_poe: buildWheatLatePoeMonths,
};

const WEED_PHENOLOGY_BY_CROP: Partial<Record<string, Partial<Record<string, WeedPhenologyBuilder>>>> = {
    soy: {
        long_fallow: buildSoyFallowMonths,
        short_fallow: buildSoyFallowMonths,
        pre_emergent: buildSoyPreEmergentMonths,
        early_poe: buildSoyEarlyPoeMonths,
    },
    wheat: cerealPhenology,
    barley: cerealPhenology,
};

const stripPhenology = (months: CropProtectionMonth[]): CropProtectionMonth[] =>
    months.map(({ icons: _icons, iconsDescriptions: _iconsDescriptions, ...month }) => month);

const resolveSoyPhenologyMoment = (applicationMoment: string) =>
    applicationMoment === 'late_poe' ? 'early_poe' : applicationMoment;

export const resolveWeedPhenologyMonths = (
    crop: string | undefined,
    applicationMoment: string,
    option: Option,
    cornMonths: CropProtectionMonth[]
): CropProtectionMonth[] => {
    const normalizedCrop = crop?.toLowerCase();
    const phenologyMoment = normalizedCrop === 'soy' ? resolveSoyPhenologyMoment(applicationMoment) : applicationMoment;
    const builder = normalizedCrop ? WEED_PHENOLOGY_BY_CROP[normalizedCrop]?.[phenologyMoment] : undefined;
    if (builder) {
        return builder(option);
    }
    if (normalizedCrop === 'corn' || !normalizedCrop) {
        return cornMonths;
    }
    return stripPhenology(cornMonths);
};

const buildCornSeedTreatmentMonths = (option: Option): CropProtectionMonth[] => {
    const sowing = getSowingPhenologyForCrop('corn');
    return [
        {
            disabled: false,
            columns: option.columns,
            icons: sowing.icons,
            iconsDescriptions: sowing.iconsDescriptions,
        },
        { disabled: true, icons: ['emergence'], iconsDescriptions: ['VE'] },
        { disabled: true, icons: ['v1'], iconsDescriptions: ['V1'] },
        { disabled: true, icons: ['v2'], iconsDescriptions: ['V2'] },
    ];
};

const buildSoySeedTreatmentMonths = (option: Option): CropProtectionMonth[] => {
    const sowing = getSowingPhenologyForCrop('soy');
    return [
        {
            disabled: false,
            columns: option.columns,
            icons: sowing.icons,
            iconsDescriptions: sowing.iconsDescriptions,
        },
        {
            disabled: true,
            icons: soyStages.vc.icons,
            iconsDescriptions: soyStages.vc.iconsDescriptions,
        },
        {
            disabled: true,
            icons: soyStages.v1.icons,
            iconsDescriptions: soyStages.v1.iconsDescriptions,
        },
        {
            disabled: true,
            icons: soyStages.v2.icons,
            iconsDescriptions: soyStages.v2.iconsDescriptions,
        },
    ];
};

const SEED_TREATMENT_PHENOLOGY_BY_CROP: Partial<Record<string, WeedPhenologyBuilder>> = {
    corn: buildCornSeedTreatmentMonths,
    soy: buildSoySeedTreatmentMonths,
    wheat: buildWheatPreEmergentMonths,
    barley: buildWheatPreEmergentMonths,
};

/** Active sowing column + following phenology stages (grayed), per crop. */
export const getSeedTreatmentMonths = (option: Option, crop?: string): CropProtectionMonth[] => {
    const normalizedCrop = crop?.toLowerCase();
    const builder = normalizedCrop ? SEED_TREATMENT_PHENOLOGY_BY_CROP[normalizedCrop] : undefined;
    if (builder) {
        return builder(option);
    }
    return buildCornSeedTreatmentMonths(option);
};
